/* eslint-disable import/no-cycle */
import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Divider, Loader } from '@mantine/core';
import { CircleCheck, CircleX } from 'tabler-icons-react';
import { Link, useNavigate } from 'react-router-dom';
import { triggerNotification } from '../../../helpers/notificationHelper';
import AppStack from '../../common/AppStack';
import AppCard from '../../common/AppCard';
import AppFlexbox from '../../common/AppFlexbox';
import AppText from '../../common/AppText';
import { useRegCartController } from '../../../helpers/shop/useRegCartController';
import { webstoreTrackingHelper } from '../../../helpers/shop/webstoreTrackingHelper';
import { Context as RegistrationContext } from '../../../providers/RegistrationProvider';

const CheckoutCompleteView = ({
  sessionId,
  secureId,
  isSuccessCode,
  invoiceId
}) => {
  const navigate = useNavigate();
  const [showInvoiceSuccess, setShowInvoiceSuccess] = useState(false);
  const [showFailedPayment, setShowFailedPayment] = useState(false);
  const { fetchCartOrderHistory } = useContext(RegistrationContext);
  const {
    checkoutSession,
    getStorageCart,
    fetchCheckoutSessionResult,
    fetchCheckoutInvoiceResult,
    clearCartBySecureId
  } = useRegCartController();
  const hasSportsPayFailed =
    checkoutSession && !checkoutSession.completed && !isSuccessCode;
  const skipCheckoutComplete = !!sessionId;

  useEffect(() => {
    if (secureId || sessionId) {
      if (!invoiceId) {
        fetchCheckoutSessionResult(
          {
            secureId: sessionId || secureId,
            skipCheckoutComplete
          },
          null,
          (e) => {
            if (!skipCheckoutComplete) {
              triggerNotification(e);
              setShowFailedPayment(true);
            }
          }
        );
      }
      else if (secureId && !skipCheckoutComplete) {
        fetchCheckoutInvoiceResult(
          {
            invoiceId,
            secureId
          },
          () => {
            setShowInvoiceSuccess(true);
          },
          (e) => {
            if (!skipCheckoutComplete) {
              triggerNotification(e);
              setShowFailedPayment(true);
            }
          }
        );
      }
      else {
        setShowInvoiceSuccess(true);
      }
    }
    else {
      navigate(`/checkout/cart`);
    }
  }, [secureId, sessionId]);

  useEffect(() => {
    if (checkoutSession) {
      const storageCart = getStorageCart();
      if (
        checkoutSession.completed &&
        checkoutSession.pkEcomOrders?.length > 0
      ) {
        fetchCartOrderHistory(checkoutSession.pkRegCart, (data) => {
          webstoreTrackingHelper.completePurchase(data, checkoutSession, true);
        });
      }

      clearCartBySecureId(checkoutSession.secureId);
    }
  }, [checkoutSession]);

  return (
    <AppStack
      style={{
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '50vh',
        padding: '40px 0px'
      }}
    >
      {!skipCheckoutComplete && (showFailedPayment || hasSportsPayFailed) ? (
        <AppCard
          radius="md"
          shadow="lg"
          style={{ padding: 20, maxWidth: 400 }}
          withBorder
        >
          <AppStack style={{ flex: 1, gap: 8 }}>
            <AppFlexbox style={{ flex: 1, justifyContent: 'center' }}>
              <CircleX color="#C40000" size={150} />
            </AppFlexbox>
            <AppStack style={{ gap: 20 }}>
              <AppStack style={{ gap: 8, textAlign: 'center' }}>
                <AppText style={{ fontSize: 24, fontWeight: 700 }}>
                  We couldn’t process your payment.
                </AppText>
                <AppText style={{ fontSize: 16, color: '#666' }}>
                  Please check your credit card statement to see if the charge
                  went through. If it did, or if the issue persists, contact our
                  support team for assistance.
                </AppText>
              </AppStack>

              <Button color="dark" component={Link} to="/checkout/cart">
                View cart
              </Button>
            </AppStack>
          </AppStack>
        </AppCard>
      ) : skipCheckoutComplete ||
        showInvoiceSuccess ||
        checkoutSession?.completed ? (
        <AppCard
          radius="md"
          shadow="lg"
          style={{ padding: 20, maxWidth: 400 }}
          withBorder
        >
          <AppStack style={{ flex: 1, gap: 8 }}>
            <AppFlexbox style={{ flex: 1, justifyContent: 'center' }}>
              <CircleCheck color="#067D62" size={150} />
            </AppFlexbox>
            <AppStack style={{ gap: 20 }}>
              <AppStack style={{ gap: 8, textAlign: 'center' }}>
                <AppText style={{ fontSize: 24, fontWeight: 700 }}>
                  Thank you!
                </AppText>

                {showInvoiceSuccess ? (
                  <AppText style={{ fontSize: 16, color: '#666' }}>
                    We’ve successfully received your payment for this invoice.
                  </AppText>
                ) : (
                  <AppText style={{ fontSize: 16, color: '#666' }}>
                    A confirmation email has been sent to your email address.
                    You can view your order status from your account order
                    history.
                  </AppText>
                )}
              </AppStack>

              <Button
                color="dark"
                component={Link}
                to={
                  checkoutSession
                    ? `/orders/${checkoutSession.pkRegCart}`
                    : `/orders`
                }
                variant="outline"
              >
                View order history
              </Button>

              <Divider label="OR" labelPosition="center" />

              <Button color="dark" component={Link} to="/leagues">
                View registrations
              </Button>
            </AppStack>
          </AppStack>
        </AppCard>
      ) : (
        <Loader color="dark" size={60} type="dots" />
      )}
    </AppStack>
  );
};

CheckoutCompleteView.propTypes = {
  invoiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isSuccessCode: PropTypes.bool,
  secureId: PropTypes.string,
  sessionId: PropTypes.string
};

export default CheckoutCompleteView;
